/**
 * R8 Feed Health illustrative sample data. The real surface uses
 * `sourceHealth(listSignals(), FEED_SOURCES, now, blockedSources)` against
 * the live snapshot; THIS file is the fallback when no live snapshot is
 * present (mirrors the §7.20 Media Coverage pattern).
 *
 * 30-day history strips are synthetic — the real ingest doesn't currently
 * persist per-source per-day state (that's Phase 7.2 with the snapshot
 * versioning work). Until then this gives the surface a visual contract
 * even on cold loads.
 */

import type { FeedState } from './types';
import type { BlockedReason } from '@biomax/plugin-surveillance-feed';

export interface SampleFeedSource {
  id: string;
  name: string;
  region: string;
  state: FeedState;
  signals: number;
  /** Days since latest signal. */
  latestDays: number;
  /** Human cadence label ('1d', '7d', '30d'). */
  cadence: string;
  history: FeedState[];
  reason?: BlockedReason;
  note?: string;
  since?: string;
}

function hist(pattern: 'steady' | 'recovered' | 'declining' | 'flapping' | 'blocked' | 'new'): FeedState[] {
  const out: FeedState[] = [];
  for (let i = 0; i < 30; i++) {
    switch (pattern) {
      case 'steady':    out.push('live'); break;
      case 'recovered': out.push(i < 18 ? 'degraded' : i < 22 ? 'stale' : 'live'); break;
      case 'declining': out.push(i < 14 ? 'live' : i < 22 ? 'stale' : i < 27 ? 'rotting' : 'degraded'); break;
      case 'flapping':  out.push((['live', 'live', 'stale', 'degraded', 'live', 'stale'] as FeedState[])[i % 6]!); break;
      case 'blocked':   out.push(i < 6 ? 'degraded' : 'blocked'); break;
      case 'new':       out.push(i < 24 ? 'idle' : 'live'); break;
    }
  }
  return out;
}

export const SAMPLE_SOURCES: SampleFeedSource[] = [
  // DEGRADED
  { id: 'cdc-nwss',  name: 'CDC NWSS', region: 'AMRO', state: 'degraded', signals: 0, latestDays: 9, cadence: '7d', history: hist('declining'), note: 'HTTP 503 on last 2 pulls — endpoint flaky', since: '31h' },
  { id: 'cov-spectrum-lapis', name: 'covSPECTRUM LAPIS', region: 'Global', state: 'degraded', signals: 0, latestDays: 4, cadence: '1d', history: hist('flapping'), note: 'auth 401 — token refresh needed', since: '4d' },
  // ROTTING
  { id: 'ecdc-erviss', name: 'ECDC ERVISS', region: 'EURO', state: 'rotting', signals: 12, latestDays: 18, cadence: '7d', history: hist('declining'), note: 'no update in 2.5× cadence', since: '18d' },
  { id: 'paho-avian-flu', name: 'PAHO Americas avian-flu', region: 'AMRO', state: 'rotting', signals: 3, latestDays: 16, cadence: '7d', history: hist('declining'), note: 'schema drift — partial parse', since: '16d' },
  // BLOCKED
  { id: 'qld-au-respiratory', name: 'QLD Health respiratory', region: 'WPRO', state: 'blocked', signals: 0, latestDays: 47, cadence: '1d', history: hist('blocked'), reason: 'antibot', note: 'every header combo → HTTP 403', since: '47d' },
  { id: 'iran-moh-sentinel',  name: 'Iran MoH sentinel',     region: 'EMRO', state: 'blocked', signals: 0, latestDays: 120, cadence: '7d', history: hist('blocked'), reason: 'sanctions',   note: 'direct ingest not permitted', since: '120d' },
  { id: 'kcdc-linelist',      name: 'KCDC line-list (KR)',   region: 'WPRO', state: 'blocked', signals: 0, latestDays: 33,  cadence: '1d', history: hist('blocked'), reason: 'geo',         note: 'firewalls non-domestic IPs',  since: '33d' },
  { id: 'nhs-england-sus',    name: 'NHS England SUS',       region: 'EURO', state: 'blocked', signals: 0, latestDays: 60,  cadence: '7d', history: hist('blocked'), reason: 'partnership', note: 'awaiting data-sharing agreement', since: '60d' },
  // STALE
  { id: 'opendengue',         name: 'OpenDengue',            region: 'SEARO', state: 'stale', signals: 412, latestDays: 22, cadence: '30d', history: hist('recovered'), note: 'past cadence — bulk source, expected lag', since: '22d' },
  { id: 'woah',               name: 'WOAH WAHIS',            region: 'Global', state: 'stale', signals: 88, latestDays: 11, cadence: '7d',  history: hist('flapping'), since: '11d' },
  { id: 'sentinelles-france', name: 'Sentinelles FR',        region: 'EURO',  state: 'stale', signals: 41, latestDays: 9,  cadence: '7d',  history: hist('recovered'), since: '9d' },
  // LIVE
  { id: 'who-don',            name: 'WHO DON',               region: 'Global', state: 'live', signals: 142, latestDays: 1, cadence: '1d', history: hist('steady') },
  { id: 'africa-cdc',         name: 'Africa CDC',            region: 'AFRO',   state: 'live', signals: 63,  latestDays: 1, cadence: '1d', history: hist('steady') },
  { id: 'afro-nigeria-ncdc-wer', name: 'NCDC Nigeria',       region: 'AFRO',   state: 'live', signals: 9,   latestDays: 0, cadence: '1d', history: hist('steady'), note: 'diphtheria 443/400/18 live' },
  { id: 'ukhsa',              name: 'UKHSA',                 region: 'EURO',   state: 'live', signals: 54,  latestDays: 1, cadence: '1d', history: hist('steady') },
  { id: 'rki-germany',        name: 'RKI ARE+SARI',          region: 'EURO',   state: 'live', signals: 38,  latestDays: 2, cadence: '7d', history: hist('steady') },
  { id: 'delphi-nssp',        name: 'Delphi NSSP',           region: 'AMRO',   state: 'live', signals: 22,  latestDays: 2, cadence: '7d', history: hist('steady') },
  { id: 'cdc-resp-net',       name: 'CDC RESP-NET',          region: 'AMRO',   state: 'live', signals: 18,  latestDays: 4, cadence: '7d', history: hist('recovered') },
  { id: 'nsw-au-respiratory', name: 'NSW Health',            region: 'WPRO',   state: 'live', signals: 11,  latestDays: 0, cadence: '1d', history: hist('steady') },
  { id: 'healthmap',          name: 'HealthMap',             region: 'Global', state: 'live', signals: 240, latestDays: 0, cadence: '1d', history: hist('steady') },
  { id: 'promed-mail',        name: 'ProMED-mail',           region: 'Global', state: 'live', signals: 96,  latestDays: 1, cadence: '1d', history: hist('steady') },
  { id: 'cbs-nl-mortality',   name: 'CBS NL',                region: 'EURO',   state: 'live', signals: 14,  latestDays: 3, cadence: '7d', history: hist('steady') },
  { id: 'phac-canada',        name: 'PHAC Canada',           region: 'AMRO',   state: 'live', signals: 27,  latestDays: 2, cadence: '7d', history: hist('steady') },
  { id: 'searo-bd-dghs-dengue', name: 'MOH Bangladesh',      region: 'SEARO',  state: 'live', signals: 6,   latestDays: 3, cadence: '7d', history: hist('steady') },
  // IDLE
  { id: 'who-weekly-bulletin', name: 'WHO weekly bulletin',  region: 'Global', state: 'idle', signals: 0, latestDays: 5,  cadence: '7d',  history: hist('new'), note: 'next publish in 2d — on schedule' },
  { id: 'nndss-quarterly',     name: 'NNDSS quarterly',      region: 'AMRO',   state: 'idle', signals: 0, latestDays: 40, cadence: '90d', history: hist('new'), note: 'quarterly — next due in 50d' },
];

/** The 'real' total — this surface knows there are ~71 sources even though
 *  the sample only renders ~26. Used by the "live N/71" KPI. */
export const TOTAL_REAL_SOURCES = 71;

// ---------------------------------------------------------------------------
// Coverage feeds (Phase 5 media — distinct from surveillance)
// ---------------------------------------------------------------------------

export interface SampleCoverageFeed {
  id: string;
  name: string;
  tier: string;
  state: FeedState | 'throttled';
  got: number;
  want: number;
  articles: number;
  note?: string;
  asymmetryFeeds?: boolean;
}

export const SAMPLE_COVERAGE_FEEDS: SampleCoverageFeed[] = [
  { id: 'gdelt', name: 'GDELT v0.1', tier: 'anonymous', state: 'throttled', got: 11, want: 18, articles: 55, note: 'soft-throttled — 11/18 windows last run', asymmetryFeeds: true },
  { id: 'promed-media', name: 'ProMED (media mode)', tier: 'n/a', state: 'live', got: 1, want: 1, articles: 12 },
  { id: 'who-sitrep', name: 'WHO sitrep scraper', tier: 'n/a', state: 'idle', got: 0, want: 1, articles: 0, note: 'next sitrep cycle in 3d' },
];

// ---------------------------------------------------------------------------
// GDELT batch queue + cost
// ---------------------------------------------------------------------------

export interface SampleQueueRow {
  id: string;
  source: string;
  running: number;
  queued: number;
  done: number;
  failed: number;
  dead: number;
  etaMin: number | null;
  articles: number;
  deadNote?: string;
}

export const SAMPLE_QUEUE: SampleQueueRow[] = [
  { id: 'q1', source: 'GDELT · Ebola DRC',     running: 1, queued: 2, done: 5, failed: 1, dead: 0, etaMin: 4,    articles: 100 },
  { id: 'q2', source: 'GDELT · Lassa NG',      running: 0, queued: 3, done: 4, failed: 2, dead: 0, etaMin: 12,   articles: 79 },
  { id: 'q3', source: 'GDELT · Cholera SD',    running: 1, queued: 1, done: 6, failed: 0, dead: 0, etaMin: 3,    articles: 83 },
  { id: 'q4', source: 'GDELT · Dengue BR',     running: 0, queued: 4, done: 3, failed: 1, dead: 0, etaMin: 18,   articles: 43 },
  { id: 'q5', source: 'GDELT · Marburg RW',    running: 0, queued: 0, done: 2, failed: 0, dead: 0, etaMin: 0,    articles: 11 },
  { id: 'q6', source: 'GDELT · Measles UK',    running: 0, queued: 1, done: 1, failed: 0, dead: 0, etaMin: 22,   articles: 4 },
  { id: 'q7', source: 'GDELT · Mpox CD',       running: 0, queued: 0, done: 0, failed: 3, dead: 3, etaMin: null, articles: 0, deadNote: 'query returns 0 after 3 attempts — needs query fix (shipped ec32271)' },
  { id: 'q8', source: 'GDELT · H5N1 US',       running: 0, queued: 0, done: 0, failed: 3, dead: 3, etaMin: null, articles: 0, deadNote: 'ambiguous pathogen-name match — dead-lettered pending fix' },
];

export interface SampleCost {
  tier: 'anonymous' | 'paid';
  todaySpend: number;
  monthSpend: number;
  monthBudget: number;
  paidProjection: number;
  throttled: boolean;
}

export const SAMPLE_COST: SampleCost = {
  tier: 'anonymous',
  todaySpend: 0,
  monthSpend: 0,
  monthBudget: 250,
  paidProjection: 138,
  throttled: true,
};
